import type { Model } from "@lumen/shared";
import { t } from "../../i18n";
import { ModelSearchPicker } from "./ModelSearchPicker";
import type { ComposerActivity, ComposerActivitySettings } from "./Composer";
import {
  TRIPO_ANIMATION_MODEL_ID,
  TRIPO_RIGGING_MODEL_ID,
} from "./ComposerParameterDefinitions";
import { ComposerDynamicParameters } from "./ComposerDynamicParameters";
import { ComposerModel3dSettings } from "./ComposerModel3dSettings";

export function ActivitySettingsPanel({
  activity,
  models,
  settings,
  onChange,
  allowModelSelection = true,
  allowAgentChoice = true,
}: {
  activity: Exclude<ComposerActivity, "ask">;
  models: Model[];
  settings: NonNullable<
    ComposerActivitySettings[Exclude<ComposerActivity, "ask">]
  >;
  onChange: (
    settings: NonNullable<
      ComposerActivitySettings[Exclude<ComposerActivity, "ask">]
    >,
  ) => void;
  allowModelSelection?: boolean;
  allowAgentChoice?: boolean;
}) {
  const selectedModel = models.find((model) => model.id === settings.modelId);
  const metadata =
    selectedModel?.remoteMetadata &&
    typeof selectedModel.remoteMetadata === "object"
      ? (selectedModel.remoteMetadata as Record<string, unknown>)
      : {};
  const rawVoices = metadata.supported_voices ?? metadata.supportedVoices;
  const voices = Array.isArray(rawVoices)
    ? rawVoices.flatMap((voice) => {
        if (typeof voice === "string") return [{ id: voice, label: voice }];
        if (!voice || typeof voice !== "object") return [];
        const value = voice as Record<string, unknown>;
        const id =
          typeof value.id === "string"
            ? value.id
            : typeof value.voice === "string"
              ? value.voice
              : null;
        return id
          ? [{ id, label: typeof value.name === "string" ? value.name : id }]
          : [];
      })
    : [];
  const isTripoRigging = selectedModel?.remoteId === TRIPO_RIGGING_MODEL_ID;
  const isTripoAnimation = selectedModel?.remoteId === TRIPO_ANIMATION_MODEL_ID;
  const isTripoModel = Boolean(
    selectedModel &&
    (selectedModel.providerName?.toLowerCase().includes("tripo") ||
      selectedModel.name.toLowerCase().includes("tripo") ||
      [
        "P1-20260311",
        "Turbo-v1.0-20250506",
        "v3.1-20260211",
        "v3.0-20250812",
      ].includes(selectedModel.remoteId ?? "")),
  );
  const isTripoGeneration =
    isTripoModel && !isTripoRigging && !isTripoAnimation;
  const isTripoHSeries = ["v3.0-20250812", "v3.1-20260211"].includes(
    selectedModel?.remoteId ?? "",
  );
  const isTripoP1 = selectedModel?.remoteId === "P1-20260311";
  const isScenarioModel =
    selectedModel?.providerName?.toLowerCase().includes("scenario") ?? false;
  const update = (patch: Partial<typeof settings>) =>
    onChange({ ...settings, ...patch });
  const emptyChoiceLabel = allowAgentChoice
    ? t("Let the agent choose")
    : t("Default");
  return (
    <div className="space-y-3 p-2">
      {allowModelSelection && (
        <ModelSearchPicker
          models={models}
          value={settings.modelId ?? ""}
          emptyLabel={t("Let the agent choose")}
          onChange={(modelId) => {
            const nextModel = models.find((model) => model.id === modelId);
            const specialized =
              nextModel?.remoteId === TRIPO_RIGGING_MODEL_ID ||
              nextModel?.remoteId === TRIPO_ANIMATION_MODEL_ID;
            update({
              modelId: modelId || undefined,
              ...(specialized &&
              settings.outputFormat &&
              !["glb", "fbx"].includes(settings.outputFormat)
                ? { outputFormat: undefined }
                : {}),
              ...(nextModel?.remoteId !== TRIPO_ANIMATION_MODEL_ID
                ? {
                    animationPreset: undefined,
                    animationPresets: undefined,
                    inputRigged: undefined,
                  }
                : {}),
            });
          }}
        />
      )}
      {(activity === "audio" || activity === "speech") && voices.length > 0 && (
        <label className="block">
          <span className="label">{t("Voice")}</span>
          <select
            className="field"
            value={settings.voice ?? ""}
            onChange={(event) =>
              update({ voice: event.target.value || undefined })
            }
          >
            <option value="">{emptyChoiceLabel}</option>
            {voices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {(activity === "image" || activity === "video") && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="label">{t("Size")}</span>
            <select
              className="field"
              value={settings.size ?? ""}
              onChange={(event) =>
                update({ size: event.target.value || undefined })
              }
            >
              <option value="">{emptyChoiceLabel}</option>
              <option value="1024x1024">1024 × 1024</option>
              <option value="1792x1024">1792 × 1024</option>
              <option value="1024x1792">1024 × 1792</option>
            </select>
          </label>
          <label className="block">
            <span className="label">
              {activity === "image" ? t("Quality") : t("Aspect ratio")}
            </span>
            <select
              className="field"
              value={
                (activity === "image"
                  ? settings.quality
                  : settings.aspectRatio) ?? ""
              }
              onChange={(event) =>
                update(
                  activity === "image"
                    ? { quality: event.target.value || undefined }
                    : { aspectRatio: event.target.value || undefined },
                )
              }
            >
              <option value="">{emptyChoiceLabel}</option>
              {activity === "image" ? (
                <>
                  <option value="standard">{t("Standard")}</option>
                  <option value="hd">HD</option>
                </>
              ) : (
                <>
                  <option value="16:9">16:9</option>
                  <option value="9:16">9:16</option>
                  <option value="1:1">1:1</option>
                </>
              )}
            </select>
          </label>
        </div>
      )}
      {(activity === "audio" ||
        activity === "speech" ||
        activity === "transcription") && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="label">{t("Format")}</span>
            <select
              className="field"
              value={settings.format ?? ""}
              onChange={(event) =>
                update({ format: event.target.value || undefined })
              }
            >
              <option value="">{emptyChoiceLabel}</option>
              {activity === "transcription" ? (
                <>
                  <option value="text">{t("Text")}</option>
                  <option value="json">{t("JSON")}</option>
                  <option value="srt">{t("SRT")}</option>
                </>
              ) : (
                <>
                  <option value="mp3">{t("MP3")}</option>
                  <option value="wav">{t("WAV")}</option>
                  <option value="opus">{t("Opus")}</option>
                </>
              )}
            </select>
          </label>
          {activity === "transcription" && (
            <label className="block">
              <span className="label">{t("Language")}</span>
              <input
                className="field"
                value={settings.language ?? ""}
                placeholder={t("Auto detect")}
                onChange={(event) =>
                  update({ language: event.target.value || undefined })
                }
              />
            </label>
          )}
        </div>
      )}
      {activity === "video" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="label">{t("Duration (seconds)")}</span>
            <input
              type="number"
              className="field"
              min={1}
              max={120}
              value={settings.duration ?? ""}
              placeholder={emptyChoiceLabel}
              onChange={(event) =>
                update({
                  duration: event.target.value
                    ? Number(event.target.value)
                    : undefined,
                })
              }
            />
          </label>
          <label className="block">
            <span className="label">{t("FPS")}</span>
            <input
              type="number"
              className="field"
              min={1}
              max={60}
              value={settings.fps ?? ""}
              placeholder={emptyChoiceLabel}
              onChange={(event) =>
                update({
                  fps: event.target.value
                    ? Number(event.target.value)
                    : undefined,
                })
              }
            />
          </label>
        </div>
      )}
      <ComposerModel3dSettings
        activity={activity}
        settings={settings}
        update={update}
        isTripoRigging={isTripoRigging}
        isTripoAnimation={isTripoAnimation}
        isTripoModel={isTripoModel}
        isTripoGeneration={isTripoGeneration}
        isTripoHSeries={isTripoHSeries}
        isTripoP1={isTripoP1}
        isScenarioModel={isScenarioModel}
      />
      <ComposerDynamicParameters
        selectedModel={selectedModel}
        settings={settings}
        onChange={onChange}
      />
      {(activity === "sprite" || activity === "tileset") &&
        !isScenarioModel && (
          <div className="grid grid-cols-3 gap-2 rounded-lg border border-lumen-200 bg-lumen-50/50 p-3 dark:border-lumen-900/50 dark:bg-lumen-950/20">
            <label className="block">
              <span className="label">{t("Columns")}</span>
              <input
                type="number"
                className="field"
                min={1}
                max={64}
                value={settings.columns ?? ""}
                placeholder="4"
                onChange={(event) =>
                  update({
                    columns: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
              />
            </label>
            <label className="block">
              <span className="label">{t("Rows")}</span>
              <input
                type="number"
                className="field"
                min={1}
                max={64}
                value={settings.rows ?? ""}
                placeholder="4"
                onChange={(event) =>
                  update({
                    rows: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
              />
            </label>
            <label className="block">
              <span className="label">{t("Tile size")}</span>
              <input
                type="number"
                className="field"
                min={1}
                max={4096}
                value={settings.tileSize ?? ""}
                placeholder="32"
                onChange={(event) =>
                  update({
                    tileSize: event.target.value
                      ? Number(event.target.value)
                      : undefined,
                  })
                }
              />
            </label>
            <label className="col-span-3 flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={settings.transparentBackground ?? false}
                onChange={(event) =>
                  update({
                    transparentBackground: event.target.checked || undefined,
                  })
                }
              />
              {t("Transparent background")}
            </label>
          </div>
        )}
      <div className="grid grid-cols-3 gap-2">
        {(
          [
            ["temperature", t("Temperature"), 0, 2, 0.1],
            ["topP", t("Top P"), 0, 1, 0.05],
            ["maxTokens", t("Max tokens"), 1, 1000000, 1],
          ] as const
        ).map(([key, label, min, max, step]) => (
          <label key={key} className="block">
            <span className="label">{label}</span>
            <input
              type="number"
              className="field min-w-0"
              min={min}
              max={max}
              step={step}
              value={typeof settings[key] === "number" ? settings[key] : ""}
              placeholder={emptyChoiceLabel}
              onChange={(event) =>
                update({
                  [key]:
                    event.target.value === ""
                      ? undefined
                      : Number(event.target.value),
                })
              }
            />
          </label>
        ))}
      </div>
    </div>
  );
}
